import { Link } from "@mui/material";
import { Link as RouterLink, useParams } from "react-router";
import { useTranslation } from "react-i18next";
import { ROUTE_PARAMS } from "@/constants";
import type { DeviceFieldsFragment } from "@/gql/graphql";

type DeviceIdentity = Pick<DeviceFieldsFragment, "serialNumber" | "modelName">;

type DeviceSerialLinkProps = {
  device: DeviceIdentity;
};

const DeviceSerialLink = ({ device }: DeviceSerialLinkProps) => {
  const { t } = useTranslation();
  const { [ROUTE_PARAMS.ACCOUNT_ID]: accountId } = useParams();
  const searchParams = new URLSearchParams();

  if (device.modelName === null || accountId === undefined) {
    return null;
  }

  searchParams.delete("search");
  searchParams.set(
    "filters",
    JSON.stringify({
      items: [
        {
          id: "device-serial-number",
          field: "serialNumber",
          operator: "equals",
          value: device.serialNumber,
        },
        {
          id: "device-model-name",
          field: "modelName",
          operator: "equals",
          value: device.modelName,
        },
      ],
    })
  );

  return (
    <Link
      component={RouterLink}
      style={{ display: "block", fontSize: "12px" }}
      state={{ resetDeviceTab: true, dismissDialogs: true }}
      to={{
        pathname: `/${accountId}/devices`,
        search: `?${searchParams.toString()}`,
      }}
    >
      {t("devices.view_device")}
    </Link>
  );
};

export default DeviceSerialLink;

const isDeviceIdentity = (value: unknown): value is DeviceIdentity => {
  if (typeof value !== "object" || value === null) {
    return false;
  }

  if (!("serialNumber" in value) || !("modelName" in value)) {
    return false;
  }

  return (
    typeof value.serialNumber === "string" &&
    (typeof value.modelName === "string" || value.modelName === null)
  );
};

export { isDeviceIdentity };
export type { DeviceIdentity };
